import React, { useState, useEffect } from 'react';
import { Button, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import useDesignerStore from './designerStore';
import NewCapturePhotoInput from './NewCapturePhotoInput';
import { renderGradientControls } from './renderGradientControls';
import { renderShadowControls } from './renderShadowControls';
import CardImagePanel from './CardImagePanel';
import { calculateFontSizeToFitBox } from './utils/textHelpers'; // adjust path
import FontPicker from "./utils/FontPicker";
import { cardSizes, getStageDimensions } from './cardSizes';
import { getAllTemplates } from './templateService';
import ColorField from './ColorField';
import { SERVER_URL } from '../../constants/server-type';
import { apiFetch } from '../../api/api';
import { toast } from 'react-toastify';
import { useOrg } from "../orgs/org-context";
import ColorSwatch from "./ColorSwatch";
import {
  polygonToPoints,
  arrowToPolygonPoints,
  scalePointsToIcon,
  circleToPolygonPoints,
  regularPolygonPoints,
  starToPolygonPoints,
  ellipseToPolygonPoints
} from './utils/shapeGeometry';
import './cards.css';
import '../../styles/Buttons.css';
// import './Buttons.css';



//
// function scalePointsToIcon(points, size = 40, padding = 4) {
//   const xs = [];
//   const ys = [];
//
//   for (let i = 0; i < points.length; i += 2) {
//     xs.push(points[i]);
//     ys.push(points[i + 1]);
//   }
//
//   const minX = Math.min(...xs);
//   const maxX = Math.max(...xs);
//   const minY = Math.min(...ys);
//   const maxY = Math.max(...ys);
//
//   const width = maxX - minX;
//   const height = maxY - minY;
//
//   const scale = Math.min(
//     (size - padding * 2) / width,
//     (size - padding * 2) / height
//   );
//
//   const offsetX = (size - width * scale) / 2;
//   const offsetY = (size - height * scale) / 2;
//
//   const result = [];
//
//   for (let i = 0; i < points.length; i += 2) {
//     result.push(
//       (points[i] - minX) * scale + offsetX,
//       (points[i + 1] - minY) * scale + offsetY
//     );
//   }
//
//   return result.join(' ');
// }
//
const shapeOptions = [
  {
    type: 'rect',
    label: 'Rectangle',
    key: 'rect',
    icon: (hovered) => {
      const color = hovered ? '#0d6efd' : '#FFA500';
      return (
        <svg width="44" height="44">
          <rect
            x="8"
            y="12"
            width="28"
            height="18"
            stroke="#000"
            strokeWidth="2"
            fill={color}
          />
        </svg>
      );
    }
  },
  {
    type: 'circle',
    label: 'Circle',
    key: 'circle',
    icon: (hovered) => {
      const color = hovered ? '#0d6efd' : '#FFA500';
      return (
        <svg width="44" height="44" viewBox="0 0 44 44">
          <circle
            cx="22"
            cy="22"
            r="11"
            stroke="#000"
            strokeWidth="2"
            fill={color}
          />
        </svg>
      );
    }
  },
  {
    type: 'line',
    label: 'Line',
    key: 'line',
    icon: (hovered) => {
      const color = hovered ? '#0d6efd' : '#FFA500';

      return (
      <svg width="40" height="40" viewBox="0 0 40 40">
        <line
          x1="5"
          y1="20"
          x2="35"
          y2="20"
          stroke={color}
          strokeWidth="2"
        />
      </svg>
    );
  }
  },
  {
    type: 'ellipse',
    label: 'Ellipse',
    key: 'ellipse',
    icon: (hovered) => {
      const color = hovered ? '#0d6efd' : '#FFA500';
      return (
        <svg width="40" height="40" viewBox="0 0 40 40">
          <ellipse
            cx="20"
            cy="20"
            rx="15"
            ry="10"
            stroke='#000000'
            strokeWidth='2'
            fill={color}
          />
        </svg>
      );
    }
  },
  {
    type: 'polygon',
    sides: 3,
    label: 'Triangle',
    key: 'polygon-3',
    icon: (hovered) => {
      const color = hovered ? '#0d6efd' : '#FFA500';
      return (
        <svg width="40" height="40" viewBox="0 0 40 40">
          <polygon points="20,5 35,35 5,35" stroke='#000000' strokeWidth='2' fill={color} />
        </svg>
      );
    }
  },
  {
    type: 'polygon',
    sides: 5,
    label: 'Pentagon',
    key: 'polygon-5',
    icon: (hovered) => {
      const color = hovered ? '#0d6efd' : '#FFA500';
      return (
        <svg width="40" height="40" viewBox="0 0 40 40">
          <polygon points="20,5 30,15 25,35 15,35 10,15" stroke='#000000' strokeWidth='2' fill={color} />
        </svg>
      );
    }
  },
  {
    type: 'polygon',
    sides: 6,
    label: 'Hexagon',
    key: 'polygon-6',
    icon: (hovered) => {
      const color = hovered ? '#0d6efd' : '#FFA500';
      return (
        <svg width="40" height="40" viewBox="0 0 40 40">
          <polygon points="20,5 30,10 30,30 20,35 10,30 10,10" stroke='#000000' strokeWidth='2' fill={color} />
        </svg>
      );
    }
  },
  {
    type: 'polygon',
    sides: 8,
    label: 'Octagon',
    key: 'polygon-8',
    icon: (hovered) => {
      const color = hovered ? '#0d6efd' : '#FFA500';
      return (
        <svg width="40" height="40" viewBox="0 0 40 40">
          <polygon points="25,5 35,10 40,20 35,30 25,35 15,30 10,20 15,10" stroke='#000000' strokeWidth='2' fill={color} />
        </svg>
      );
    }
  },
  {
    type: 'star',
    label: 'Star',
    key: 'star',
    icon: (hovered) => {
      const color = hovered ? '#0d6efd' : '#FFA500';
      return (
        <svg width="40" height="40" viewBox="0 0 40 40">
          <polygon points="20,3 24,15 36,15 26,23 30,35 20,28 10,35 14,23 4,15 16,15" stroke='#000000' strokeWidth='2' fill={color} />
        </svg>
      );
    }
  },
  {
    type: 'arrow',
    label: 'Arrow',
    key: 'arrow',
    icon: (hovered) => {
      const arrow = arrowToPolygonPoints({
        length: 160,
        shaftWidth: 40,
        headLength: 50,
        headWidth: 80
      });

      const svgPoints = scalePointsToIcon(arrow);

      return (
        <svg width="40" height="40" viewBox="0 0 40 40">
          <polygon
            points={svgPoints}
            stroke="#000000"
            strokeWidth="2"
            fill={hovered ? '#0000FF' : '#FFA500'}
          />
        </svg>
      );
    }
  }
];

// function polygonToPoints({
//   sides,
//   radius,
//   rotation = -Math.PI / 2,
//   centerX = 0,
//   centerY = 0
// }) {
//   const pts = [];
//
//   for (let i = 0; i < sides; i++) {
//     const angle = rotation + (i * 2 * Math.PI) / sides;
//
//     const x = radius * Math.cos(angle);
//     const y = radius * Math.sin(angle);
//
//     pts.push(x, y);
//   }
//
//   return pts;
// }

const SidePanel = () => {
  const {
    fontOptions,
    selectedIds,
    objects,
    updateObject,
    scale,
    cardSettings,
    settingTransitionWarnings,
    updateCardSetting,
    activeTool,
    setActiveTool,
    addObject,
    setSelectedIds,
    addImage,
    setScale,
    name,
    setName,
    cardId,
    gridSize,
    setGridSize,
    description,
    setDescription,
    selectedTemplateId,
    setSelectedTemplateId,
    showGrid,
    toggleGrid,
    isSnapToGridEnabled,
    toggleSnapToGrid,
    generateId,
    flushTransforms
  } = useDesignerStore();


  const selectedObject = objects.find(obj => obj.id === selectedIds[0]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [useGradient, setUseGradient] = useState(false);
  const [gradientStops, setGradientStops] = useState([]);
  const [gradientDirection, setGradientDirection] = useState('diagonal');
  const [hoveredShapeType, setHoveredShapeType] = useState(null);
  const [hoveredShapeKey, setHoveredShapeKey] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [design, updateDesign] = useState([]);

  // const [datasets, setDataSets] = useState([]);
  // const [datasetsLoading, setDataSetsLoading] = useState(false);
  // const [datasetsError, setDataSetsError] = useState(null);
  const activeSurface = useDesignerStore(s => s.activeSurface);
  const setActiveSurface = useDesignerStore(s => s.setActiveSurface);
  const { activeOrg } = useOrg();

  const selectedObj =
    selectedIds.length === 1
      ? objects.find(o => o.id === selectedIds[0])
      : null;

  const isBackgroundSelected = !!selectedObj?.isBackground;

    useEffect(() => {
      if (!cardId) return;

      // Reset panel when card changes
      setActiveTool('info');
    }, [cardId]);



  useEffect(() => {
    if (activeSurface === 'back' && cardSettings.sides !== 'Duplex') {
      setActiveSurface('front');
    }

    if (activeSurface === 'frontSilver' && cardSettings.silverFront !== 'yes') {
      setActiveSurface('front');
    }

    if (activeSurface === 'backSilver') {
      if (cardSettings.sides !== 'Duplex' || cardSettings.silverBack !== 'yes') {
        setActiveSurface('front');
      }
    }
  }, [
    cardSettings.sides,
    cardSettings.silverFront,
    cardSettings.silverBack,
    activeSurface,
    setActiveSurface
  ]);

  useEffect(() => {
    getAllTemplates().then(setTemplates).catch(console.error);
  }, []);



  useEffect(() => {
    if (!selectedObject) return;
    setUseGradient(!!selectedObject.fillLinearGradientColorStops);
    setGradientStops(
      selectedObject.fillLinearGradientColorStops
        ? selectedObject.fillLinearGradientColorStops.filter((_, i) => i % 2 === 1)
        : [selectedObject.fill || '#cccccc']
    );
    setGradientDirection(selectedObject.gradientDirection || 'diagonal');
  }, [selectedObject]);

  // function arrowToPolygonPoints({
  //   length = 160,
  //   shaftWidth = 20,
  //   headLength = 40,
  //   headWidth = 40
  // }) {
  //   const halfShaft = shaftWidth / 2;
  //   const halfHead = headWidth / 2;
  //   const shaftEnd = length - headLength;
  //
  //   const cx = length / 2;   // center shift
  //
  //   return [
  //     // TOP
  //     -cx, -halfShaft,
  //     shaftEnd - cx, -halfShaft,
  //
  //     shaftEnd - cx, -halfHead,
  //     length - cx, 0,
  //
  //     shaftEnd - cx, halfHead,
  //     shaftEnd - cx, halfShaft,
  //
  //     -cx, halfShaft
  //   ];
  // }


  const handleAddShape = (shapeType, sides = null) => {
    const base = {
      id: Date.now().toString(),
      type: shapeType,
      x: 150,
      y: 150,
      stroke: '#000000',
      strokeWidth: 1,
      strokeScaleEnabled: false,
      opacity: 1,
      draggable: true,
    };

    let shapeConfig = {};

    switch (shapeType) {

      case 'rect':
        shapeConfig = {
          width: 100,
          height: 60,
          fill: '#FF0000',
          cornerRadius: 0
        };
        break;

        case 'circle': {
          const circle = {
            radius: 50,
            segments: 32
          };

          shapeConfig = {
            type: 'circle',
            points: circleToPolygonPoints(circle),
            closed: true,
            fill: '#000080',

            meta: {
              ...circle
            }
          };

          break;
        }

        case 'line':
          shapeConfig = {
            type: 'line',
            points: [0, 0, 120, 0],
            stroke: '#000000',
            strokeWidth: 2,
            closed: false,
            meta: { type: 'line' }
          };
          break;

        case 'ellipse': {
          const ellipse = {
            radiusX: 60,
            radiusY: 40,
            segments: 32,
          };

          shapeConfig = {
            type: 'ellipse',
            points: ellipseToPolygonPoints(ellipse),
            closed: true,
            fill: '#800080',
            meta: ellipse,
          };

          break;
        }

      case 'polygon': {
        const poly = {
          sides: sides || 5,
          radius: 50
        };

        shapeConfig = {
          points: polygonToPoints(poly),
          closed: true,
          fill: '#00ff00',
          meta: poly
        };

        break;
      }

      case 'star': {
        const star = {
          type: 'star',
          numPoints: 5,
          innerRadius: 20,
          outerRadius: 50,
        };

        shapeConfig = {
          type: 'star',
          points: starToPolygonPoints(star),
          closed: true,
          fill: '#FFD700',
          meta: star,
        };

        break;
      }


      case 'arrow': {
        const arrow = {
          length: 160,
          shaftWidth: 20,
          headLength: 40,
          headWidth: 40
        };

        shapeConfig = {
          points: arrowToPolygonPoints(arrow),
          closed: true,
          fill: '#000000',
          meta: {
            type: 'arrow',
            ...arrow
          }
        };

        break;
      }

      default:
        return;
    }

    const newObject = { ...base, ...shapeConfig };
    addObject(newObject);
    setSelectedIds([newObject.id]);
  };


  const handleTemplateSelect = (templateId) => {

    if (!activeOrg?._id) {
      toast.warn('Please select an organization before choosing a template');
      return;
    }

    setSelectedTemplateId(templateId);
  };





  const renderSettingsPanel = () => (

    <>
      <h4>Settings</h4>

      {/* Inline: short text field */}
      <Form.Group className="mb-2">
        <Form.Label>Card Name</Form.Label>
        <Form.Control
          type="text"
          value={name || ''}
          onChange={(e) => setName(e.target.value)}
        />
      </Form.Group>

      {/* Stacked: description can grow / long text */}
      <Form.Group className="mb-2 stacked">
        <Form.Label>Description</Form.Label>
        <Form.Control
          type="text"
          value={description || ''}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Form.Group>

      <Form.Group className="mb-2 stacked">
        <label >Template:</label>
        <select
          value={selectedTemplateId || ''}
          onChange={(e) => {
            handleTemplateSelect(e.target.value);
          }}
        >
          <option value="">— Select Template —</option>
          {templates.map(tpl => (
            <option key={tpl._id} value={tpl._id}>
              {tpl.name}
            </option>
          ))}
        </select>
      </Form.Group>


<hr/><br/>


      {/* Inline: select fits comfortably */}
      <Form.Group className="mb-2">
        <Form.Label>Card Size</Form.Label>
        <Form.Select
          value={cardSettings.size}
          onChange={(e) => updateCardSetting('size', e.target.value)}
        >
          <option value="cr80">CR80</option>
          <option value="4x6">4x6</option>
        </Form.Select>
      </Form.Group>

      { cardSettings.size === "cr80" && (<>
      <Form.Group className="mb-2 stacked">
        <Form.Label>Sides</Form.Label>
        <Form.Select
          value={cardSettings.sides}
          onChange={(e) => updateCardSetting('sides', e.target.value)}>
          <option value="Simplex">Front only</option>
          <option value="Duplex">Front + Back</option>
        </Form.Select>
      </Form.Group>

      <Form.Group className="mb-2">
        <Form.Label>Silver Front</Form.Label>
        <Form.Select
          value={cardSettings.silverFront}
          onChange={(e) => updateCardSetting('silverFront', e.target.value)}>
          <option value="no">No</option>
          <option value="yes">Yes</option>
        </Form.Select>
      </Form.Group>

      {/* Only show silver back when duplex */}
      {cardSettings.sides === 'Duplex' && (
        <Form.Group className="mb-2">
          <Form.Label>Silver Back</Form.Label>
          <Form.Select
            value={cardSettings.silverBack}
              onChange={(e) => updateCardSetting('silverBack', e.target.value)}>
            <option value="no">No</option>
            <option value="yes">Yes</option>
          </Form.Select>
        </Form.Group>
      )}</>
    )}

      {/* Inline: select fits comfortably */}
      <Form.Group className="mb-2">
        <Form.Label>Orientation</Form.Label>
        <Form.Select
          value={cardSettings.format}
          onChange={(e) => updateCardSetting('format', e.target.value)}
        >
          <option value="portrait">Portrait</option>
          <option value="landscape">Landscape</option>
        </Form.Select>
      </Form.Group>
      {settingTransitionWarnings.length > 0 && (
        <div className="card-setting-warning" role="status">
          {settingTransitionWarnings.length} object{settingTransitionWarnings.length === 1 ? '' : 's'}
          {' '}could not be reduced without crossing a text or QR legibility limit.
          <ul>
            {settingTransitionWarnings.map(warning => (
              <li key={`${warning.surface}-${warning.objectId}`}>
                {warning.surface}: {warning.objectType} — {warning.reason}
              </li>
            ))}
          </ul>
        </div>
      )}
      <hr/>
      <Form.Group className="mb-2">
        <button className={`${showGrid ? 'btn-push snapon soft' : 'btn-white soft'}`} onClick={toggleGrid}>
          Grid Display
        </button>
        <button className={`${isSnapToGridEnabled ? 'btn-push snapon soft' : 'btn-white soft'}`} onClick={toggleSnapToGrid}>
          Grid Snap
        </button>
      </Form.Group>

      <Form.Group className="mb-2">
        <Form.Label>
          Grid Size&nbsp;
          <span style={{ fontSize: '0.65rem', opacity: 0.7 }}>
            ({gridSize}px)
          </span>
        </Form.Label>

        <Form.Range
          min={5}
          max={100}
          step={5}
          value={gridSize}
          onChange={(e) => setGridSize(Number(e.target.value))}
        />
      </Form.Group>
      <div className="panel-action-wrap">
        <button onClick={addBackgroundFill} className="btn-save soft panel-action-LG">
          Background Fill
        </button>
      </div>
    </>
  );


  const addBackgroundFill = () => {
    const surface = activeSurface;

    // Prevent duplicates
    const existing = objects.find(o => o.isBackground);
    if (existing) {
      toast.info('Background fill already exists');
      return;
    }
    const { cardWidth, cardHeight} = getStageDimensions(cardSettings);
    const newObject = {
      id: Date.now().toString(),
      type: 'rect',
      x: 0,
      y: 0,
      width: cardWidth,
      height: cardHeight,
      fill: 'blue',
      cornerRadius: 0,
      stroke: '#000000',
      strokeWidth: 1,
      strokeScaleEnabled: false,
      opacity: 1,
      draggable: false,
      isBackground: true
    };

    addObject(newObject);
    setSelectedIds([newObject.id]);
  };


  const renderShapeList = () => (
    <>
      <h4>Shapes</h4><br/>
      <div className="shape-grid">
        {shapeOptions.map((shape) => {
          const isHovered = hoveredShapeKey === shape.key;

          return (
            <OverlayTrigger
              key={shape.key}
              placement="top"
              overlay={<Tooltip>{shape.label}</Tooltip>}
            >
              <div
                className="mb-2 d-flex align-items-center justify-content-center border rounded"
                style={{
                  marginBottom:'20px',
                  width: '64px',
                  height: '64px',
                  maxWidth: '64px',
                  // height: '42px',
                  cursor: 'pointer',
                  flexDirection: 'column',
                  position: 'relative',
                  backgroundColor: isHovered ? '#e6f0ff' : 'transparent',
                }}
                onClick={() => handleAddShape(shape.type, shape.sides)}
                onMouseEnter={() => setHoveredShapeKey(shape.key)}
                onMouseLeave={() => setHoveredShapeKey(null)}
              >
                <div>{shape.icon(isHovered)}</div>

              </div>
            </OverlayTrigger>
          );
        })}<br/>
      </div>

    </>
  );

  const expandHex = (hex) => {
    if (hex.length === 4) {
      return '#' + hex[1] + hex[1] + hex[2] + hex[2] + hex[3] + hex[3];
    }
    return hex;
  };

  const renderBackgroundAttributes = (shape) => {
    return (
      <div className="p-2">
        <h4>Background Attributes</h4>

        <Form.Group className="mb-2" style={{textAlign:'top'}}>
          <ColorSwatch
            label="Fill Color:"
            value={shape.fill}
            onChange={(color) =>
              updateObject(shape.id, { fill: color })
            }
            allowTransparent
          />
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label>Opacity:</Form.Label>
          <Form.Range
            min={0}
            max={1}
            step={0.01}
            value={shape.opacity || 1}
            onChange={(e) => updateObject(shape.id, { opacity: parseFloat(e.target.value) })}
          />
        </Form.Group>
        {renderGradientControls({
          shape,
          useGradient,
          setUseGradient,
          gradientStops,
          setGradientStops,
          gradientDirection,
          setGradientDirection,
          updateObject,
          scale
        })}
        <Form.Group className="mb-2">
        <ColorSwatch
          label="Border Color:"
          value={shape.stroke}
          onChange={(color) =>
            updateObject(shape.id, { stroke: color })
          }
          allowTransparent
        />
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label>Border Width:</Form.Label>
          <Form.Control
            type="number"
            min="0"
            style={{ width: '50px' }}  // 👈 Adjust as needed
            value={shape.strokeWidth || 1}
            onChange={(e) => updateObject(shape.id, { strokeWidth: parseInt(e.target.value) })}
          />
        </Form.Group>

        {shape?.type === 'rect' && (
          <Form.Group className="mb-2">
            <Form.Label>Corner Radius:</Form.Label>
            <Form.Control
              type="number"
              min="0"
              style={{ width: '50px' }}  // 👈 Adjust as needed
              value={selectedObject.cornerRadius || 0}
              onChange={(e) => updateObject(selectedObject.id, { cornerRadius: parseInt(e.target.value, 10) })}
            />
          </Form.Group>
        )}
        <Form.Group className="mb-2">
          <Form.Label>Border Style:</Form.Label>
          <Form.Select
            value={shape.dash ? 'dashed' : 'solid'}
            onChange={(e) => {
              const value = e.target.value;
              updateObject(shape.id, { dash: value === 'dashed' ? [10, 5] : [] });
            }}
          >
            <option value="solid">Solid</option>
            <option value="dashed">Dashed</option>
          </Form.Select>
        </Form.Group>
      </div>
    );
  }


  const renderShapeAttributes = (shape) => {
    const hasPlaceholder = shape.imagePlaceholder?.previewSrc;

    if (selectedObject.type === 'image') {
      return (
      <div className="p-2">
        <div className="panel-header" style={{padding:"21px 20px 0px 5px"}}>
          <h4>Image Attributes</h4>
        </div>

        <div className="shape-attributes" style={{padding:"10px 10px 0px 10px"}}>
        <div style={{padding:"0px 0px 10px 13px"}}>
          {/* Opacity */}
          <div className="attr-row stacked">
            <label>Opacity</label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={selectedObject.opacity}
              onChange={(e) =>
                updateObject(selectedObject.id, {
                  opacity: parseFloat(e.target.value),
                })
              }
            />
          </div>

          {/* Brightness */}
          <div className="attr-row stacked">
            <label>Brightness</label>
            <input
              type="range"
              min="-1"
              max="1"
              step="0.1"
              value={selectedObject.brightness ?? 0}
              onChange={(e) =>
                updateObject(selectedObject.id, {
                  brightness: parseFloat(e.target.value),
                })
              }
            />
          </div>

          {/* Contrast */}
          <div className="attr-row stacked">
            <label>Contrast</label>
            <input
              type="range"
              min="-100"
              max="100"
              step="1"
              value={selectedObject.contrast ?? 0}
              onChange={(e) =>
                updateObject(selectedObject.id, {
                  contrast: parseFloat(e.target.value),
                })
              }
            />
          </div>

          {/* Grayscale */}
          <div className="attr-row stacked">
            <label>Grayscale</label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={selectedObject.grayscale ?? 0}
              onChange={(e) =>
                updateObject(selectedObject.id, {
                  grayscale: parseFloat(e.target.value),
                })
              }
            />
          </div>

          {/* Sepia */}
          <div className="attr-row stacked">
            <label>Sepia</label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={selectedObject.sepia ?? 0}
              onChange={(e) =>
                updateObject(selectedObject.id, {
                  sepia: parseFloat(e.target.value),
                })
              }
            />
          </div>

          {/* Rotation */}
          <div className="attr-row stacked">
            <label>Rotation</label>
            <input
              type="range"
              min="0"
              max="360"
              step="1"
              value={selectedObject.rotation ?? 0}
              onChange={(e) =>
                updateObject(selectedObject.id, {
                  rotation: parseFloat(e.target.value),
                })
              }
            />
          </div></div>

          {/* Border color (already consistent) */}
          <Form.Group className="mb-2">
            <ColorSwatch
              label="Border Color:"
              value={shape.stroke}
              onChange={(color) =>
                updateObject(shape.id, { stroke: color })
              }
              allowTransparent
            />
          </Form.Group>
          {/* Border width */}
          <Form.Group className="mb-2">
            <Form.Label>Border Width</Form.Label>
            <Form.Control
              type="number"
              min="0"
              style={{ width: '50px' }}
              value={selectedObject.strokeWidth || 1}
              onChange={(e) =>
                updateObject(selectedObject.id, {
                  strokeWidth: parseInt(e.target.value, 10),
                })
              }
            />
          </Form.Group>

          {/* Corner radius */}
          <Form.Group className="mb-2">
            <Form.Label>Corner Radius</Form.Label>
            <Form.Control
              type="number"
              min="0"
              style={{ width: '50px' }}
              value={selectedObject.cornerRadius || 0}
              onChange={(e) =>
                updateObject(selectedObject.id, {
                  cornerRadius: parseInt(e.target.value, 10),
                })
              }
            />
          </Form.Group>

          {/* Border style */}
          <Form.Group className="mb-2">
            <Form.Label>Border Style</Form.Label>
            <Form.Select
              value={selectedObject.dash?.length ? 'dashed' : 'solid'}
              onChange={(e) => {
                const value = e.target.value;
                updateObject(selectedObject.id, {
                  dash: value === 'dashed' ? [10, 5] : [],
                });
              }}
            >
              <option value="solid">Solid</option>
              <option value="dashed">Dashed</option>
            </Form.Select>
          </Form.Group>
        </div>
      </div>
      );
    }
    else {
      const isFillTransparent =
        (!shape.fill || shape.fill === 'transparent') &&
        !shape.useGradient;

      return (
      <div className="p-2">
        <h4>Shape Attributes</h4>

        <Form.Group className="mb-2" style={{textAlign:'top'}}>
          <ColorSwatch
            label="Fill Color:"
            value={shape.fill}
            onChange={(color) => {
              const isNowTransparent =
                (!color || color === 'transparent') && !shape.useGradient;

              updateObject(shape.id, {
                fill: color,

                // 🔥 auto-clear shadow if the shape becomes hollow
                ...(isNowTransparent && {
                  shadowBlur: 0,
                  shadowOffsetX: 0,
                  shadowOffsetY: 0,
                  shadowOpacity: 0
                })
              });
            }}
            allowTransparent
          />
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label>Opacity:</Form.Label>
          <Form.Range
            min={0}
            max={1}
            step={0.01}
            value={shape.opacity || 1}
            onChange={(e) => updateObject(shape.id, { opacity: parseFloat(e.target.value) })}
          />
        </Form.Group>
        {renderGradientControls({
          shape,
          useGradient,
          setUseGradient,
          gradientStops,
          setGradientStops,
          gradientDirection,
          setGradientDirection,
          updateObject,
          scale
        })}
        {!isFillTransparent && renderShadowControls({
          shape,
          updateObject,
        })}
        <Form.Group className="mb-2">
          <ColorSwatch
            label="Border Color:"
            value={shape.stroke}
            onChange={(color) =>
              updateObject(shape.id, { stroke: color })
            }
            allowTransparent
          />
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label>Border Width:</Form.Label>
          <Form.Control
            type="number"
            min="0"
            style={{ width: '50px' }}  // 👈 Adjust as needed
            value={shape.strokeWidth ?? 0}
            onChange={(e) =>
                updateObject(shape.id, {
                  strokeWidth: Number.isNaN(parseInt(e.target.value))
                    ? 0
                    : parseInt(e.target.value)
                })
              }
          />
        </Form.Group>

        {shape?.type === 'rect' && (
          <Form.Group className="mb-2">
            <Form.Label>Corner Radius:</Form.Label>
            <Form.Control
              type="number"
              min="0"
              style={{ width: '50px' }}  // 👈 Adjust as needed
              value={selectedObject.cornerRadius || 0}
              onChange={(e) => updateObject(selectedObject.id, { cornerRadius: parseInt(e.target.value, 10) })}
            />
          </Form.Group>
        )}
        <Form.Group className="mb-2">
          <Form.Label>Border Style:</Form.Label>
          <Form.Select
            value={shape.dash ? 'dashed' : 'solid'}
            onChange={(e) => {
              const value = e.target.value;
              updateObject(shape.id, { dash: value === 'dashed' ? [10, 5] : [] });
            }}
          >
            <option value="solid">Solid</option>
            <option value="dashed">Dashed</option>
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label>
            Rotation: <strong>{shape.rotation || 0}°</strong>
          </Form.Label>
          <Form.Range
            min={0}
            max={360}
            step={1}
            value={shape.rotation || 0}
            onChange={(e) =>
              updateObject(shape.id, { rotation: parseInt(e.target.value) })
            }
          />
        </Form.Group>
      </div>
    );
  }
}

  const renderLineAttributes = (shape) => (
    <>
      <h4>Line Attributes</h4>

      <Form.Group className="mb-2">
        <Form.Label>Line Width:</Form.Label>
        <Form.Control
          type="number"
          style={{width:"50px"}}
          value={shape.strokeWidth || 1}
          onChange={(e) => updateObject(shape.id, { strokeWidth: parseInt(e.target.value) })}
        />
      </Form.Group>

      <Form.Group className="mb-2">
        <Form.Label><br/>Line Color:</Form.Label>
        <Form.Control
          type="color"
          value={(shape.stroke || '#ffffff').length === 4 ? expandHex(shape.fill) : shape.fill}
          onChange={(e) => updateObject(shape.id, { stroke: e.target.value })}
        />
      </Form.Group>

      <Form.Group className="mb-2">
        <Form.Label><br/>Line Style:</Form.Label>
        <Form.Select
          value={shape.dash?.length ? 'dashed' : 'solid'}
          onChange={(e) => updateObject(shape.id, {
            dash: e.target.value === 'dashed' ? [10, 5] : []
          })}
        >
          <option value="solid">Solid</option>
          <option value="dashed">Dashed</option>
        </Form.Select>
      </Form.Group>

      <Form.Group className="mb-2">
        <Form.Label>
          <br/>Rotation: <strong>{shape.rotation || 0}°</strong>
        </Form.Label>
        <Form.Range
          min={0}
          max={360}
          step={1}
          value={shape.rotation || 0}
          onChange={(e) =>
            updateObject(shape.id, { rotation: parseInt(e.target.value) })
          }
        />
      </Form.Group>

      <Form.Group className="mb-2">
        <Form.Label>Opacity:<br/></Form.Label>
        <Form.Range
          min={0}
          max={1}
          step={0.01}
          value={shape.opacity || 1}
          onChange={(e) => updateObject(shape.id, { opacity: parseFloat(e.target.value) })}
        />
      </Form.Group>
  </>
);

const handleAddTextObject = () => {

  const defaultFont =
    (fontOptions && fontOptions.length > 0)
      ? fontOptions[0]
      : "";

  const newText = {
    id: `text-${Date.now()}`,
    type: "text",

    x: 250,
    y: 250,

    width: 360,
    height: 120,

    padding: 6,

    text: "New Text",

    fontFamily: defaultFont,
    fontSize: 80,
    fontStyle: "normal",
    textDecoration: "none",

    fill: "#000000",

    align: "center",
    verticalAlign: "middle",

    textFrameMode: "shrink",   // ⭐ best default for badges
    lineHeight: 1.1,

    draggable: true
  };

  addObject(newText);
  setSelectedIds([newText.id]);

};


const renderTextToolPanel = () => (
  <div className="p-2">
  <div className="panel-header" style={{padding:"21px 20px 0px 5px"}}>
    <h4>Text</h4>
    <button
      className="btn-white soft panel-action"
      onClick={handleAddTextObject}
    >
      Add
    </button>
  </div>


    <div style={{padding:"21px 20px 0px 20px", fontSize: 12, opacity: 0.75 }}>
      Select a text object to edit its properties.
    </div>
  </div>
);


const renderQRToolPanel = () => (
  <div className="p-2">
  <div className="panel-header" style={{padding:"21px 20px 0px 5px"}}>
    <h4>QR Codes</h4>
    <button
      className="btn-white soft panel-action"
      onClick={handleAddQRObject}
    >
      Add
    </button>
  </div>

    <div style={{padding:"21px 20px 0px 20px", fontSize: 12, opacity: 0.75 }}>
      Select a QR code object to edit its properties.
    </div>
  </div>
);



const handleAddQRObject = () => {
  const newQR = {
        id: Date.now().toString(),
        type: 'qrcode',
        x: 200,
        y: 200,
        width: 200,
        height: 200,
        qrValue: 'MY URL',
        qrColor: '#000000',
        qrBackground: '#ffffff',
        qrQuietZone: 12,
        qrQuietZoneWidth: 0,
        qrShadow: {
          color: 'transparent',
          blur: 0,
          offsetX: 0,
          offsetY: 0
        },
        draggable: true,
        rotation: 0,
        density: 1.0
    };



    // text: 'New Text',
    // fontSize: 80,
    // fontFamily: 'Arial',
    // fill: '#000000',
    // align: 'left',
    // fontStyle: 'normal',
    // textDecoration: 'none',

  addObject(newQR);
  setSelectedIds([newQR.id]); // now renderTextAttributes will kick in
};

const renderQRAttributes = (qrObj) => {
  const {
    id,
    qrValue = '',
    qrColor = '#000000',
    qrBackground = '#ffffff',
    opacity = 1,
    qrQuietZone = 12,
    width = 200,
    rotation = 0
  } = qrObj;

  return (
    <>
      <h4>QR Code Settings</h4>

      <div className="p-2">

        {/* Content */}
        <Form.Group className="mb-2">
          <Form.Label>Content</Form.Label>
          <Form.Control
            value={qrValue}
            onChange={(e) =>
              updateObject(id, { qrValue: e.target.value })
            }
          />
        </Form.Group>

        {/* Size (square) */}
        <Form.Group className="mb-2">
          <Form.Label>Size</Form.Label>
          <Form.Range
            min={40}
            max={600}
            step={1}
            value={width}
            onChange={(e) => {
              const size = parseInt(e.target.value, 10);
              updateObject(id, { width: size, height: size });
            }}
          />
        </Form.Group>

        {/* Foreground color */}
        <Form.Group className="mb-2">
          <Form.Label>QR Color</Form.Label>
          <ColorSwatch
            value={qrColor}
            onChange={(c) =>
              updateObject(id, { qrColor: c })
            }
          />
        </Form.Group>

        {/* Background color */}
        <Form.Group className="mb-2">
          <Form.Label>Background</Form.Label>
          <ColorSwatch
            value={qrBackground}
            onChange={(c) =>
              updateObject(id, { qrBackground: c })
            }
            allowTransparent
          />
        </Form.Group>

        {/* Opacity */}
        <Form.Group className="mb-2">
          <Form.Label>Opacity</Form.Label>
          <Form.Range
            min={0}
            max={1}
            step={0.01}
            value={opacity}
            onChange={(e) =>
              updateObject(id, { opacity: parseFloat(e.target.value) })
            }
          />
        </Form.Group>

        {/* Quiet zone */}
        <Form.Group className="mb-2">
          <Form.Label>Quiet Zone:</Form.Label>
          <Form.Control
            type="number"
            min="0"
            style={{ width: '50px' }}  // 👈 Adjust as needed
            value={qrQuietZone || 1}
            onChange={(e) => updateObject(id, { qrQuietZone: parseInt(e.target.value) })}
          />
        </Form.Group>



          <Form.Group className="mb-2">
            <Form.Label>
              Rotation: <strong>{rotation || 0}°</strong>
            </Form.Label>
            <Form.Range
              min={0}
              max={360}
              step={1}
              value={rotation || 0}
              onChange={(e) =>
                updateObject(id, { rotation: parseInt(e.target.value) })
              }
            />
          </Form.Group>

      </div>
    </>
  );
};

useEffect(() => {
  console.log("Loaded fonts:", fontOptions);
}, [fontOptions]);

const renderTextAttributes = (textObj) => (
  <>
    <div className="panel-header" style={{ padding: "21px 20px 0px 0px" }}>
      <h4>Text Settings</h4>
      <button
        className="btn-white soft panel-action"
        onClick={handleAddTextObject}
      >
        Add
      </button>
    </div>

    {/* CONTENT */}
    <Form.Group className="mb-2">
      <Form.Label>Content</Form.Label>
      <Form.Control
        as="textarea"
        rows={4}
        value={textObj.text || ''}
        onChange={(e) =>
          updateObject(textObj.id, { text: e.target.value })
        }
      />
    </Form.Group>

    {/* FONT */}
    <Form.Group className="mb-2">
      <Form.Label>Font</Form.Label>

      <FontPicker
        value={textObj.fontFamily}
        options={fontOptions}
        onChange={(font) =>
          updateObject(textObj.id, { fontFamily: font })
        }
      />

    </Form.Group>

    {/* FONT SIZE */}
    <Form.Group className="mb-2">
      <Form.Label>Font Size</Form.Label>
      <Form.Control
        type="number"
        style={{ width: '70px' }}
        value={textObj.fontSize ?? 24}
        onChange={(e) =>
          updateObject(textObj.id, {
            fontSize: parseInt(e.target.value)
          })
        }
        disabled={textObj.textFrameMode === 'shrink'}
      />
    </Form.Group>

    {/* LINE HEIGHT */}
    <Form.Group className="mb-2">
      <Form.Label>Line Height</Form.Label>
      <Form.Control
        type="number"
        step="0.1"
        style={{ width: '70px' }}
        value={textObj.lineHeight ?? 1.2}
        onChange={(e) =>
          updateObject(textObj.id, {
            lineHeight: parseFloat(e.target.value)
          })
        }
      />
    </Form.Group>
    <Form.Group className="mb-2">
      <Form.Label>Padding</Form.Label>

      <Form.Control
        type="number"
        step="1"
        style={{ width: '70px' }}
        value={textObj.padding ?? 6}
        onChange={(e) =>
          updateObject(textObj.id, {
            padding: parseInt(e.target.value)
          })
        }
      />
    </Form.Group>
    {/* TEXT FRAME MODE */}
    <Form.Group className="mb-2">
      <Form.Label>Text Behavior</Form.Label>

      <Form.Select
        value={textObj.textFrameMode || 'fixed'}
        onChange={(e) =>
          updateObject(textObj.id, {
            textFrameMode: e.target.value
          })
        }
      >
        <option value="fixed">Fixed</option>
        <option value="auto">Auto Height</option>
        <option value="shrink">Shrink To Fit</option>
      </Form.Select>
    </Form.Group>

    {/* WRAP WIDTH */}
    <Form.Group className="mb-2">
      <Form.Label>Wrap Width</Form.Label>
      <Form.Control
        type="number"
        step="0.01"
        style={{ width: '90px' }}
        value={(textObj.width ?? 200).toFixed(2)}
        onChange={(e) =>
          updateObject(textObj.id, {
            width: parseFloat(parseFloat(e.target.value).toFixed(2))
          })
        }
      />
    </Form.Group>

    {/* HEIGHT (only relevant for shrink) */}
    {textObj.textFrameMode === 'shrink' && (
      <Form.Group className="mb-2">
        <Form.Label>Frame Height</Form.Label>
        <Form.Control
          type="number"
          style={{ width: '70px' }}
          value={textObj.height || 120}
          onChange={(e) =>
            updateObject(textObj.id, {
              height: parseInt(e.target.value)
            })
          }
        />
      </Form.Group>
    )}

    {/* ALIGNMENT */}
    <Form.Group className="mb-2">
      <Form.Label>Alignment</Form.Label>

      <Form.Select
        value={textObj.align || 'left'}
        onChange={(e) =>
          updateObject(textObj.id, { align: e.target.value })
        }
      >
        <option value="left">Left</option>
        <option value="center">Center</option>
        <option value="right">Right</option>
        <option value="justify">Justify</option>
      </Form.Select>
    </Form.Group>

    <Form.Group className="mb-2">
      <Form.Label>Vertical Alignment</Form.Label>

      <Form.Select
        value={textObj.verticalAlign || 'top'}
        onChange={(e) =>
          updateObject(textObj.id, { verticalAlign: e.target.value })
        }
      >
        <option value="top">Top</option>
        <option value="middle">Middle</option>
        <option value="bottom">Bottom</option>
      </Form.Select>
    </Form.Group>

    {/* FONT STYLE */}
    <Form.Group className="mb-2">
      <Form.Label>Font Style</Form.Label>

      <div className="font-style-group">
        {['normal', 'bold', 'italic', 'bold italic'].map(style => (
          <Form.Check
            key={style}
            inline
            label={style}
            name="fontStyle"
            type="radio"
            id={`style-${style}`}
            checked={textObj.fontStyle === style}
            onChange={() =>
              updateObject(textObj.id, { fontStyle: style })
            }
          />
        ))}

        <span className="font-style-divider" />

        <Form.Check
          inline
          type="checkbox"
          label="Underline"
          id="style-underline"
          checked={textObj.textDecoration === 'underline'}
          onChange={(e) =>
            updateObject(textObj.id, {
              textDecoration: e.target.checked ? 'underline' : 'none'
            })
          }
        />
      </div>
    </Form.Group>

    {/* COLOR */}
    <Form.Group className="mb-2">
      <ColorSwatch
        label="Font Color"
        value={expandHex(textObj.fill || '#ffffff')}
        onChange={(color) =>
          updateObject(textObj.id, { fill: color })
        }
        allowTransparent
      />
    </Form.Group>
  </>
);

  const renderPhotoToolPanel = () => (
    <div className="p-2">
      <div className="panel-header" style={{ padding: "21px 20px 0px 5px" }}>
        <h4>Photo</h4>
        <button
          className="btn-white soft panel-action"
          onClick={handleAddPhotoFrame}
        >
          Add
        </button>
      </div>

      <div style={{ padding: "21px 20px 0px 20px", fontSize: 12, opacity: 0.75 }}>
        Add a photo frame, user photos will map to this location.
      </div>
      <NewCapturePhotoInput />
    </div>
  );

  const handleAddPhotoFrame = () => {
    const { cardWidth, cardHeight} = getStageDimensions(cardSettings);
    console.log("cardWidth:", cardWidth);
    console.log("cardHeight:", cardHeight);
    const aspectRatio = 4 / 3;

    const frameWidth = Math.round(cardWidth * 0.6);
    const frameHeight = Math.round(frameWidth / aspectRatio);
    console.log("frameWidth:", frameWidth);
    console.log("frameHeight:", frameHeight);
    const photo = {
      id: Date.now().toString(),
      type: 'photo',
      x: Math.round((cardWidth - frameWidth) / 2),
      y: Math.round((cardHeight- frameHeight) / 2),
      width: frameWidth,
      height: frameHeight,
      draggable: true,
      opacity: 1,
      imagePlaceholder: {
        fitMode: 'cover'
      }
    };

    useDesignerStore.getState().addCapturedPhotoInput(photo);
    setSelectedIds([photo.id]);
  };

  // const fitFrameInsideCard = ({
  //   cardWidth,
  //   cardHeight,
  //   aspect,          // width / height (portrait = 3/4, landscape = 4/3, etc.)
  //   maxFrac = 0.6    // how much of the card the frame may occupy
  // }) => {
  //   // Max allowed box for the frame
  //   const maxW = cardWidth * maxFrac;
  //   const maxH = cardHeight* maxFrac;
  //
  //   // Fit by width first, then clamp by height if needed
  //   let w = maxW;
  //   let h = w / aspect;
  //
  //   if (h > maxH) {
  //     h = maxH;
  //     w = h * aspect;
  //   }
  //
  //   return { w: Math.round(w), h: Math.round(h) };
  // };

  // --- helpers (drop-in near the top of SidePanel.js) ---
  const round2 = (n) => Math.round((n || 0) * 100) / 100;

  const fitFrameInsideCard = ({ cardWidth, cardHeight, aspect, maxFrac = 0.6 }) => {
    // Start with width-based sizing
    let w = Math.round(cardWidth * maxFrac);
    let h = Math.round(w / aspect);

    // If too tall, switch to height-based sizing
    const maxH = Math.round(cardHeight * maxFrac);
    if (h > maxH) {
      h = maxH;
      w = Math.round(h * aspect);
    }

    // Final clamp just in case
    w = Math.max(1, Math.min(w, cardWidth));
    h = Math.max(1, Math.min(h, cardHeight));

    return { w, h };
  };

  // --- drop-in renderPhotoAttributes(photo) ---
  const renderPhotoAttributes = (photo) => {
    const { cardWidth, cardHeight } = getStageDimensions(cardSettings);

    const currentWidth = Number(photo.width);
    const currentHeight = Number(photo.height);
    const aspect = currentWidth > 0 && currentHeight > 0
      ? currentWidth / currentHeight
      : 4 / 3;

    const fullCard = () => {
      updateObject(photo.id, {
        x: 0,
        y: 0,
        width: cardWidth,
        height: cardHeight,
        rotation: 0, // ✅ undo rotation
        imagePlaceholder: {
          ...(photo.imagePlaceholder || {}),
          fitMode: "cover",
        },
      });
    };

    const centerFrame = () => {
      const w = photo.width ?? Math.round(cardWidth * 0.6);
      const h = photo.height ?? Math.round(cardHeight * 0.6);

      updateObject(photo.id, {
        x: Math.round((cardWidth - w) / 2),
        y: Math.round((cardHeight - h) / 2),
        rotation: 0, // ✅ undo rotation
        imagePlaceholder: {
          ...(photo.imagePlaceholder || {}),
          fitMode: "cover",
        },
      });
    };

    const resetFrame = () => {
      // Refit without changing the frame's current shape.
      const { w: newW, h: newH } = fitFrameInsideCard({
        cardWidth,
        cardHeight,
        aspect,
        maxFrac: 0.6,
      });

      updateObject(photo.id, {
        x: Math.round((cardWidth - newW) / 2),
        y: Math.round((cardHeight - newH) / 2),
        width: newW,
        height: newH,
        rotation: 0, // ✅ undo rotation
        imagePlaceholder: {
          ...(photo.imagePlaceholder || {}),
          fitMode: "cover", // ✅ always cover
        },
      });
    };

    return (
      <div className="p-2">
        <h4>Photo Frame</h4>
        <div style={{padding:"0px 20px 0px 20px"}}>
          <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 12,padding: "0 0 0 6px" }}>
            The placeholder is editor-only. Capture controls photo fit, pan, zoom and crop.
          </div>


        {/* --- Photo “Image Attributes” (same as image) --- */}
        <div className="shape-attributes" style={{ padding: "10px 10px 0px 0" }}>
          <div style={{ padding: "0px 0px 10px 6px" }}>
            {/* Opacity */}
            <div className="attr-row stacked">
              <label>Opacity</label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={photo.opacity ?? 1}
                onChange={(e) =>
                  updateObject(photo.id, { opacity: parseFloat(e.target.value) })
                }
              />
            </div>

            {/* Brightness */}
            <div className="attr-row stacked">
              <label>Brightness</label>
              <input
                type="range"
                min="-1"
                max="1"
                step="0.1"
                value={photo.brightness ?? 0}
                onChange={(e) =>
                  updateObject(photo.id, { brightness: parseFloat(e.target.value) })
                }
              />
            </div>

            {/* Contrast */}
            <div className="attr-row stacked">
              <label>Contrast</label>
              <input
                type="range"
                min="-100"
                max="100"
                step="1"
                value={photo.contrast ?? 0}
                onChange={(e) =>
                  updateObject(photo.id, { contrast: parseFloat(e.target.value) })
                }
              />
            </div>

            {/* Grayscale (stored 0..1, applied as on/off in Konva) */}
            <div className="attr-row stacked">
              <label>Grayscale</label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={photo.grayscale ?? 0}
                onChange={(e) =>
                  updateObject(photo.id, { grayscale: parseFloat(e.target.value) })
                }
              />
            </div>

            {/* Sepia (stored 0..1, applied as on/off in Konva) */}
            <div className="attr-row stacked">
              <label>Sepia</label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={photo.sepia ?? 0}
                onChange={(e) =>
                  updateObject(photo.id, { sepia: parseFloat(e.target.value) })
                }
              />
            </div>

            {/* Rotation */}
            <div className="attr-row stacked">
              <label>Rotation</label>
              <input
                type="range"
                min="0"
                max="360"
                step="1"
                value={photo.rotation ?? 0}
                onChange={(e) =>
                  updateObject(photo.id, { rotation: parseFloat(e.target.value) })
                }
              />
            </div>
          </div>

          {/* Border color */}
          <Form.Group style={{padding:"0px 0px 4px 0px"}}>
            <ColorSwatch
              label="Border Color:"
              value={photo.stroke}
              onChange={(color) => updateObject(photo.id, { stroke: color })}
              allowTransparent
            />
          </Form.Group>

          {/* Border width */}
          <Form.Group style={{padding:"0px 0px 4px 0px"}}>
            <Form.Label>Border Width</Form.Label>
            <Form.Control
              type="number"
              min="0"
              style={{ width: "50px" }}
              value={photo.strokeWidth ?? 1}
              onChange={(e) =>
                updateObject(photo.id, { strokeWidth: parseInt(e.target.value, 10) })
              }
            />
          </Form.Group>

          {/* Corner radius */}
          <Form.Group style={{padding:"0px 0px 4px 0px"}}>
            <Form.Label>Corner Radius</Form.Label>
            <Form.Control
              type="number"
              min="0"
              style={{ width: "50px" }}
              value={photo.cornerRadius ?? 0}
              onChange={(e) =>
                updateObject(photo.id, { cornerRadius: parseInt(e.target.value, 10) })
              }
            />
          </Form.Group>

          {/* Border style */}
          <Form.Group style={{padding:"0px 0px 10px 0px"}}>
            <Form.Label>Border Style</Form.Label>
            <Form.Select
              value={(photo.dash?.length ?? 0) ? "dashed" : "solid"}
              onChange={(e) => {
                const value = e.target.value;
                updateObject(photo.id, { dash: value === "dashed" ? [10, 5] : [] });
              }}
            >
              <option value="solid">Solid</option>
              <option value="dashed">Dashed</option>
            </Form.Select>
          </Form.Group>
          <div style={{ fontSize: ".7rem", fontWeight: "bold", padding: "10px 0 6px 10px" }}>
            Quick actions
          </div>

          <div className="d-flex gap-2 flex-wrap">
            <button type="button" className="btn-white soft" onClick={fullCard}>
              Full Card
            </button>

            <button type="button" className="btn-white soft" onClick={centerFrame}>
              Center
            </button>

            <button type="button" className="btn-white soft" onClick={resetFrame}>
              Reset
            </button>
          </div>
        </div>
        </div>

      </div>
    );
  };

  const renderPanelContent = () => {

    const selectedObject = objects.find(obj => obj.id === selectedIds[0]);

    // Always honor selected object panels first
    if (selectedObject?.type === 'photo') return renderPhotoAttributes(selectedObject);
    if (selectedObject?.type === 'text') return renderTextAttributes(selectedObject);
    if (selectedObject?.type === 'line') return renderLineAttributes(selectedObject);
    if (selectedObject?.type === 'qrcode') return renderQRAttributes(selectedObject);
    if ((selectedObject) && isBackgroundSelected) return renderBackgroundAttributes(selectedObject);
    if ((selectedObject) && !isBackgroundSelected) return renderShapeAttributes(selectedObject);

    // Tool panels (no selection)
    if (activeTool === 'images') return <CardImagePanel activeOrgId={activeOrg._id}/>;
    if (activeTool === 'shapes') return renderShapeList();
    if (activeTool === 'text') return renderTextToolPanel();
    if (activeTool === 'photo') return renderPhotoToolPanel();
    if (activeTool === 'qrcode') return renderQRToolPanel();
    if ((activeTool === 'info') || (!activeTool)) return renderSettingsPanel();

  };


  return (
    <div className="side-panel">
      <div className="side-panel-content">
        {renderPanelContent()}
      </div>
    </div>
  );
};

export default SidePanel;
